import React from 'react';
import styled from 'styled-components';
import { breakpoints } from '../../tokens/breakpoints';
import { CARD_PADDING } from '../../constants';
import { popoverBoxshadow } from '../../sharedStyles';

export type CardVariant = 'shadow' | 'border';

export type CardProps = {
  children: React.ReactNode;
  desktopWidth?: string;
  desktopMinHeight?: string;
  desktopHeight?: string;
  variant?: CardVariant;
  noGap?: boolean;
  noPadding?: boolean;
};

const BaseCard = styled.div<{
  $desktopWidth: string;
  $desktopMinHeight: string;
  $desktopHeight: string;
  $variant: CardVariant;
  $noGap: boolean;
}>`
  display: flex;
  border-radius: var(--border-radius-large);
  background: var(--white);
  ${({ $variant }) => ($variant === 'shadow' ? popoverBoxshadow : 'box-shadow: none')};
  border: ${({ $variant }) => ($variant === 'border' ? '1px solid var(--grayscale-100)' : 'none')};
  position: relative;
  overflow: hidden;
  align-items: center;
  gap: ${({ $noGap }) => ($noGap ? '0px' : '48px')};
  flex-shrink: 0;

  @media (min-width: ${breakpoints.largeDesktopMin}px) {
    width: ${({ $desktopWidth }) => $desktopWidth};
    min-height: ${({ $desktopMinHeight }) => $desktopMinHeight};
    height: ${({ $desktopHeight }) => $desktopHeight};
  }
`;

const StyledHorizontalCard = styled(BaseCard)<{
  $desktopWidth: string;
  $desktopMinHeight: string;
  $desktopHeight: string;
  $noGap: boolean;
  $variant: CardVariant;
}>`
  padding: 12px;
  flex-direction: row;
`;

const StyledCard = styled(BaseCard)<{
  $desktopWidth: string;
  $desktopMinHeight: string;
  $desktopHeight: string;
  $noGap: boolean;
  $variant: CardVariant;
  $noPadding: boolean;
}>`
  display: flex;
  padding: ${({ $noPadding }) => ($noPadding ? '0px' : `${CARD_PADDING}px`)};
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.mobileMax}px) {
    padding: ${({ $noPadding }) => ($noPadding ? '0px' : '20px')};
  }
`;

const BASE_STRIP_HEIGHT = 6;

export const CardStrip = styled.div<{ $backgroundColor: string }>`
  width: 100%;
  height: ${BASE_STRIP_HEIGHT}px;
  border-radius: ${BASE_STRIP_HEIGHT}px ${BASE_STRIP_HEIGHT}px 0 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: ${({ $backgroundColor }) => $backgroundColor};
`;

export const CardStateSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 88px;
  border-radius: 8px;
  width: 100%;
`;

const DEFAULT_CARD_DESKTOP_WIDTH = '489px';

export const Card = ({
  children,
  desktopWidth = DEFAULT_CARD_DESKTOP_WIDTH,
  desktopMinHeight = 'auto',
  desktopHeight = 'auto',
  variant = 'shadow',
  noGap = false,
  noPadding = false,
}: CardProps) => {
  return (
    <StyledCard
      $desktopWidth={desktopWidth}
      $desktopMinHeight={desktopMinHeight}
      $desktopHeight={desktopHeight}
      $noGap={noGap}
      $variant={variant}
      $noPadding={noPadding}
    >
      {children}
    </StyledCard>
  );
};

export const HorizontalCard = ({
  children,
  desktopWidth = DEFAULT_CARD_DESKTOP_WIDTH,
  desktopMinHeight = 'auto',
  desktopHeight = 'auto',
  variant = 'border',
  noGap = false,
}: CardProps) => {
  return (
    <StyledHorizontalCard
      $desktopWidth={desktopWidth}
      $desktopMinHeight={desktopMinHeight}
      $desktopHeight={desktopHeight}
      $noGap={noGap}
      $variant={variant}
    >
      {children}
    </StyledHorizontalCard>
  );
};

export default Card;
